/**
 * الدعم الفني — messages to the support team, real people.
 *
 *   SupportCenter      the user's side, inside the help panel: their
 *                      conversations, a new message with a screenshot of the
 *                      screen they are on (taken the moment they start one,
 *                      with the help panel left out), and each conversation
 *   SupportThread      one conversation — bubbles, pictures, reply box;
 *                      shared with the platform admin's inbox (AdminSupport)
 *
 * Backend/routes/support.js. Replies arrive by polling; the launcher's dot
 * says when support has answered.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Icon } from '../../components/common';
import { useNotifications } from '../../context/NotificationContext';
import { supportApi } from '../../api/endpoints';
import { usePoll } from '../../hooks/usePolling';
import { captureScreen, readImageFile } from './screenshot';

export const SUPPORT_STATUS = Object.freeze({
  open: { label: 'بانتظار الدعم', tone: 'amber', icon: 'hourglass' },
  answered: { label: 'تم الرد', tone: 'emerald', icon: 'check-circle-2' },
  closed: { label: 'مغلقة', tone: 'slate', icon: 'lock' },
});

const when = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' });
};

export function StatusChip({ status }) {
  const meta = SUPPORT_STATUS[status] || SUPPORT_STATUS.open;
  return (
    <span className={`support-chip support-chip--${meta.tone}`}>
      <Icon name={meta.icon} size={11} /> {meta.label}
    </span>
  );
}

/** A message's picture, fetched with the session and shown from a blob URL. */
function MessageImage({ message }) {
  const [url, setUrl] = useState(null);
  const [big, setBig] = useState(false);
  useEffect(() => {
    let alive = true;
    let objectUrl = null;
    supportApi
      .imageBlob(message.id)
      .then((blob) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [message.id]);
  if (!url) return <span className="support-image support-image--loading"><Icon name="image" size={16} /></span>;
  return (
    <>
      <button type="button" className="support-image" onClick={() => setBig(true)} title={message.attachmentName || 'صورة'}>
        <img src={url} alt={message.attachmentName || 'صورة مرفقة'} />
        {message.isScreenshot && (
          <span className="support-image__tag">
            <Icon name="monitor" size={10} /> لقطة الشاشة
          </span>
        )}
      </button>
      {big && (
        <div className="support-lightbox" role="dialog" aria-label="عرض الصورة" onClick={() => setBig(false)}>
          <img src={url} alt="" />
        </div>
      )}
    </>
  );
}

/** Attach: the current screen (default) or a picked picture. */
function AttachPicker({ image, onImage, autoCapture = false }) {
  const { error: toastError } = useNotifications();
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const capture = useCallback(async () => {
    setBusy(true);
    try {
      onImage({ dataUrl: await captureScreen(), screenshot: true });
    } catch (err) {
      toastError(`تعذر التقاط الشاشة: ${err.message || err}`);
    } finally {
      setBusy(false);
    }
  }, [onImage, toastError]);

  useEffect(() => {
    if (autoCapture) capture();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="support-attach">
      {image ? (
        <div className="support-attach__preview">
          <img src={image.dataUrl} alt="المرفق" />
          <div className="min-w-0 flex-1 text-[11px]">
            <p className="font-bold text-white">{image.screenshot ? 'لقطة للشاشة الحالية' : 'صورة مرفقة'}</p>
            <p className="text-slate-400">تُرسل مع الرسالة ليرى فريق الدعم ما تراه.</p>
          </div>
          <button type="button" className="help-icon-btn" onClick={() => onImage(null)} title="إزالة المرفق">
            <Icon name="trash-2" size={14} />
          </button>
        </div>
      ) : (
        <p className="text-[11px] text-slate-400">{busy ? 'جارٍ التقاط الشاشة…' : 'بدون مرفق'}</p>
      )}
      <div className="flex flex-wrap gap-1.5">
        <button type="button" className="help-chip" onClick={capture} disabled={busy}>
          <Icon name={busy ? 'loader-2' : 'camera'} size={13} className={busy ? 'animate-spin' : ''} /> {image?.screenshot ? 'إعادة الالتقاط' : 'لقطة للشاشة'}
        </button>
        <button type="button" className="help-chip" onClick={() => fileRef.current?.click()}>
          <Icon name="image-plus" size={13} /> صورة من الجهاز
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            try {
              if (file) onImage({ dataUrl: await readImageFile(file), screenshot: false });
            } catch (err) {
              toastError(err.message);
            }
          }}
        />
      </div>
    </div>
  );
}

/** One conversation: the messages and the reply box. */
export function SupportThread({ ticketId, asSupport = false, onBack = null, onChanged = null }) {
  const { success, error: toastError } = useNotifications();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [body, setBody] = useState('');
  const [image, setImage] = useState(null);
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setData(await supportApi.ticket(ticketId));
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, [ticketId]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);
  usePoll(load, 15000, { immediate: false });
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [data?.messages?.length]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await supportApi.reply(ticketId, { body: body.trim(), image: image?.dataUrl || null, isScreenshot: Boolean(image?.screenshot) });
      setBody('');
      setImage(null);
      await load();
      onChanged?.();
    } catch (err) {
      toastError(err.message);
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await supportApi.setStatus(ticketId, status);
      success(status === 'closed' ? 'تم إغلاق المحادثة' : 'أعيد فتح المحادثة');
      await load();
      onChanged?.();
    } catch (err) {
      toastError(err.message);
    }
  };

  if (error) return <p className="p-4 text-sm text-red-300">{error.message}</p>;
  if (!data) return <p className="p-4 text-center text-xs text-slate-400">جارٍ التحميل…</p>;
  const { ticket, messages } = data;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="support-thread__head">
        {onBack && (
          <button type="button" className="help-icon-btn" onClick={onBack} aria-label="رجوع">
            <Icon name="chevron-right" size={16} />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-black text-white">{ticket.subject}</p>
          <p className="truncate text-[10.5px] text-slate-400">
            {asSupport ? `${ticket.userName || '—'} · ${ticket.roleTitle || ''}${ticket.institutionName ? ` · ${ticket.institutionName}` : ''}` : `#${ticket.id} · ${when(ticket.createdAt)}`}
          </p>
          {asSupport && ticket.pagePath && (
            <p className="truncate text-[10px] text-slate-500" dir="ltr">
              {ticket.pagePath}
            </p>
          )}
        </div>
        <StatusChip status={ticket.status} />
        {ticket.status !== 'closed' ? (
          <button type="button" className="help-icon-btn" onClick={() => setStatus('closed')} title="إغلاق المحادثة">
            <Icon name="check-circle-2" size={15} />
          </button>
        ) : (
          <button type="button" className="help-icon-btn" onClick={() => setStatus('open')} title="إعادة فتح">
            <Icon name="rotate-ccw" size={15} />
          </button>
        )}
      </div>

      <div ref={listRef} className="help-messages custom-scrollbar">
        {messages.map((m) => {
          const mine = asSupport ? m.senderSide === 'support' : m.senderSide === 'user';
          return (
            <div key={m.id} className={`help-bubble ${mine ? 'help-bubble--user' : 'help-bubble--bot'} ${m.senderSide === 'support' ? 'help-bubble--support' : ''}`}>
              {!mine && (
                <div className="help-bubble__who">
                  <span className="help-bubble__avatar" aria-hidden="true">
                    <Icon name={m.senderSide === 'support' ? 'headset' : 'user'} size={12} />
                  </span>
                  {m.senderSide === 'support' ? 'فريق الدعم' : m.senderName || ticket.userName || 'المستخدم'}
                </div>
              )}
              <p className="whitespace-pre-wrap">{m.body}</p>
              {m.imagePath && <MessageImage message={m} />}
              <p className="help-bubble__time">{when(m.createdAt)}</p>
            </div>
          );
        })}
      </div>

      <div className="support-reply">
        {image && <AttachPicker image={image} onImage={setImage} />}
        <div className="help-composer !border-0 !p-0">
          <textarea
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={asSupport ? 'اكتب ردك للمستخدم…' : 'اكتب رسالتك…'}
            className="help-input"
            maxLength={5000}
            aria-label="الرسالة"
          />
          {!image && (
            <button
              type="button"
              className="help-icon-btn"
              title="إرفاق لقطة للشاشة"
              onClick={async () => {
                try {
                  setImage({ dataUrl: await captureScreen(), screenshot: true });
                } catch (err) {
                  toastError(`تعذر التقاط الشاشة: ${err.message || err}`);
                }
              }}
            >
              <Icon name="camera" size={16} />
            </button>
          )}
          <button type="button" className="help-send" onClick={send} disabled={sending || !body.trim()} aria-label="إرسال">
            <Icon name={sending ? 'loader-2' : 'send'} size={16} className={sending ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
    </div>
  );
}

/** The user's side: conversations, a new one, and each one. */
export function SupportCenter({ onRead = null }) {
  const location = useLocation();
  const { success, error: toastError } = useNotifications();
  const [view, setView] = useState({ name: 'list' });
  const [tickets, setTickets] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [image, setImage] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      setTickets(await supportApi.tickets({ mine: '1' }));
    } catch {
      setTickets([]);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    try {
      const ticket = await supportApi.create({
        subject: subject.trim(),
        body: body.trim(),
        pagePath: location.pathname,
        image: image?.dataUrl || null,
        isScreenshot: Boolean(image?.screenshot),
      });
      success('وصلت رسالتك لفريق الدعم — سيصلك الرد هنا');
      setSubject('');
      setBody('');
      setImage(null);
      await load();
      setView({ name: 'thread', id: ticket.id });
    } catch (err) {
      toastError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (view.name === 'thread') {
    return (
      <SupportThread
        ticketId={view.id}
        onBack={() => {
          setView({ name: 'list' });
          load();
          onRead?.();
        }}
        onChanged={load}
      />
    );
  }

  if (view.name === 'new') {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 custom-scrollbar">
        <button type="button" className="help-chip self-start" onClick={() => setView({ name: 'list' })}>
          <Icon name="chevron-right" size={13} /> رجوع
        </button>
        <label className="block">
          <span className="mb-1 block text-xs font-bold text-slate-300">العنوان</span>
          <input className="help-input !min-h-[42px]" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={160} placeholder="مثال: الإيصال لا يُطبع" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-bold text-slate-300">التفاصيل</span>
          <textarea className="help-input" rows={4} value={body} onChange={(e) => setBody(e.target.value)} maxLength={5000} placeholder="اكتب ما حدث وما كنت تحاول فعله…" />
        </label>
        <AttachPicker image={image} onImage={setImage} autoCapture />
        <button type="button" className="help-primary" disabled={sending || !subject.trim() || !body.trim()} onClick={create}>
          <Icon name={sending ? 'loader-2' : 'send'} size={15} className={sending ? 'animate-spin' : ''} /> إرسال لفريق الدعم
        </button>
        <p className="text-[10.5px] text-slate-500">يصل لفريق الدعم اسمك ودورك والشاشة التي كنت عليها مع اللقطة.</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="p-4 pb-2">
        <button type="button" className="help-primary w-full" onClick={() => setView({ name: 'new' })}>
          <Icon name="message-square" size={15} /> رسالة جديدة لفريق الدعم
        </button>
        <p className="mt-2 text-center text-[10.5px] text-slate-500">فريق حقيقي يرد عليك — تُرفق لقطة للشاشة الحالية تلقائياً.</p>
      </div>
      <div className="help-messages custom-scrollbar !pt-0">
        {!tickets ? (
          <p className="text-center text-xs text-slate-400">جارٍ التحميل…</p>
        ) : tickets.length === 0 ? (
          <p className="py-6 text-center text-xs text-slate-400">لا توجد محادثات بعد.</p>
        ) : (
          tickets.map((t) => (
            <button key={t.id} type="button" className="support-row" onClick={() => setView({ name: 'thread', id: t.id })}>
              <span className="support-row__icon" aria-hidden="true">
                <Icon name="messages-square" size={15} />
              </span>
              <span className="min-w-0 flex-1 text-right">
                <span className="block truncate text-sm font-bold text-white">{t.subject}</span>
                <span className="block truncate text-[10.5px] text-slate-400">{when(t.lastMessageAt || t.createdAt)}</span>
              </span>
              {t.unreadForUser && <span className="support-row__dot" aria-label="رد جديد" />}
              <StatusChip status={t.status} />
            </button>
          ))
        )}
      </div>
    </div>
  );
}

export default SupportCenter;
